import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { availableRemaining } from '../lib/run'
import { todayKey } from '../lib/datetime'
import { reportSaveError } from '../lib/saveError'

// ── useRuns ───────────────────────────────────────────────────────────────────
// The user's active "Runs" (self-paced binge plans), keyed by show_id. Start a
// run with a pace or a deadline; the slice/standing/projection are derived in
// run.js. Safe-by-default: if the runs table doesn't exist yet (migration not
// run), loads stay empty and starting a run no-ops rather than throwing.
export function useRuns(user) {
  const userId = user?.id
  const [runs, setRuns] = useState({})   // show_id -> run row
  const [ready, setReady] = useState(false)
  const [enabled, setEnabled] = useState(false)   // false until the table is confirmed present

  const load = useCallback(async () => {
    if (!userId) { setRuns({}); setReady(true); setEnabled(false); return }
    const { data, error } = await supabase
      .from('runs')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
    if (error) {
      // Table-missing (migration not run yet) is expected and silent. Anything else
      // (permissions, network) also disables the feature but is worth a trace —
      // otherwise it silently vanishes with zero debugging signal (Jason 2026-07-03).
      if (error.code !== '42P01') console.warn('[useRuns] load failed', error)
      setReady(true); setEnabled(false); return
    }
    setEnabled(true)
    const map = {}
    for (const r of (data || [])) map[r.show_id] = r
    setRuns(map)
    setReady(true)
  }, [userId])

  useEffect(() => { load() }, [load])

  // Start (or replace) a run for a show.
  // opts: { mode:'pace'|'deadline', pace?, targetDate?, show, progress }
  const start = useCallback(async (showId, opts) => {
    if (!userId) return null
    const remaining = availableRemaining(opts.show, opts.progress, todayKey())
    const row = {
      user_id: userId,
      show_id: Number(showId),
      mode: opts.mode,
      pace: opts.mode === 'pace' ? Math.max(1, opts.pace || 1) : null,
      target_date: opts.mode === 'deadline' ? opts.targetDate : null,
      start_progress: opts.progress || null,
      remaining_at_start: remaining,
      started_at: todayKey(),
      status: 'active',
    }
    const { data, error } = await supabase.from('runs')
      .upsert(row, { onConflict: 'user_id,show_id' })
      .select().single()
    if (reportSaveError(error, 'start run')) return null   // surface failure instead of a silent no-op
    setRuns(prev => ({ ...prev, [showId]: data }))
    return data
  }, [userId])

  // Change ONLY the pace/target of an active run — preserves the baseline
  // (start_progress, remaining_at_start, started_at), unlike start() which
  // re-stamps them. So editing "how many a night" doesn't wipe your progress.
  const updatePace = useCallback(async (showId, { mode, pace, targetDate }) => {
    if (!userId) return null
    const patch = {
      mode,
      pace:        mode === 'pace' ? Math.max(1, pace || 1) : null,
      target_date: mode === 'deadline' ? targetDate : null,
    }
    const prev = runs[showId]
    setRuns(p => (prev ? { ...p, [showId]: { ...prev, ...patch } } : p))  // optimistic
    const { data, error } = await supabase.from('runs').update(patch)
      .eq('user_id', userId).eq('show_id', Number(showId)).select().single()
    if (reportSaveError(error, 'update run')) { if (prev) setRuns(p => ({ ...p, [showId]: prev })); return null }
    setRuns(p => ({ ...p, [showId]: data }))
    return data
  }, [userId, runs])

  const end = useCallback(async (showId, status = 'abandoned') => {
    if (!userId) return
    const prev = runs[showId]
    setRuns(p => { const n = { ...p }; delete n[showId]; return n })
    const { error } = await supabase.from('runs').update({ status }).eq('user_id', userId).eq('show_id', Number(showId))
    // Optimistic removal desync: if the status write fails the run is still `active`
    // in the DB (and would block a new run via the user_id,show_id upsert). Restore it.
    if (reportSaveError(error, 'end run') && prev) setRuns(p => ({ ...p, [showId]: prev }))
  }, [userId, runs])

  return { runs, ready, enabled, start, updatePace, end, reload: load }
}
